/**
 * TikTok Pixel Provider
 * Handles TikTok Pixel event tracking
 * Uses ad-blocker-resistant naming
 */

import { createDebugLogger } from '../../../utils/debugLogger';

const logger = createDebugLogger('tiktok');

interface TikTokPixelConfig {
    pixelId: string;
}

/**
 * TikTok Pixel event mapping
 * Maps standard event names to TikTok Pixel events
 */
const TIKTOK_EVENT_MAP: Record<string, string> = {
    signup_started: 'InitiateCheckout',
    signup_step_viewed: 'ViewContent',
    signup_step_completed: 'AddToCart',
    product_selected: 'AddToCart',
    payment_method_created: 'AddPaymentInfo',
    signup_completed: 'CompleteRegistration',
    conversion_completed: 'CompletePayment',
    payment_method_failed: 'AddPaymentInfo',
    error_occurred: 'ViewContent',
    button_clicked: 'SubmitForm',
    form_field_completed: 'SubmitForm',
};

/**
 * Initialize TikTok Pixel
 * Matches TikTok's official base code exactly
 */
export const initializeTikTokPixel = (config: TikTokPixelConfig): void => {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if already initialized
    if ((window as any).ttq && (window as any).ttq._i && (window as any).ttq._i[config.pixelId]) {
        return;
    }

    // TikTok's official base code pattern
    (function(w: any, d: any, t: string) {
        // Set the analytics object name (required by TikTok's remote script)
        w.TiktokAnalyticsObject = t;
        
        // Initialize ttq as an array (not a function)
        const ttq = w[t] = w[t] || [];
        
        // Define all TikTok Pixel methods
        ttq.methods = [
            "page", "track", "identify", "instances", "debug", 
            "on", "off", "once", "ready", "alias", "group", 
            "enableCookie", "disableCookie", "holdConsent", 
            "revokeConsent", "grantConsent"
        ];
        
        // setAndDefer creates stub methods that queue calls
        ttq.setAndDefer = function(t: any, e: string) {
            t[e] = function() {
                // eslint-disable-next-line prefer-rest-params -- verbatim TikTok Pixel bootstrap snippet
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
        };
        
        // Create stub methods for all methods in the methods array
        for (let i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        
        // instance function for multiple pixels
        ttq.instance = function(t: string) {
            const e = ttq._i[t] || [];
            for (let n = 0; n < ttq.methods.length; n++) {
                ttq.setAndDefer(e, ttq.methods[n]);
            }
            return e;
        };
        
        // load function - creates and loads the script
        ttq.load = function(e: string, n?: any) {
            const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
            const o = n && n.partner;
            
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = r;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            
            // Create script tag with query parameters
            const script = d.createElement("script");
            script.type = "text/javascript";
            script.async = true;
            script.src = r + "?sdkid=" + e + "&lib=" + t;
            
            const firstScript = d.getElementsByTagName("script")[0];
            firstScript.parentNode.insertBefore(script, firstScript);
        };
        
        // Load the pixel
        ttq.load(config.pixelId);
        
        // Call page() immediately (as per TikTok's code)
        ttq.page();
    })(window, document, 'ttq');
};

/**
 * Track event with TikTok Pixel
 */
export const trackTikTokEvent = (
    eventName: string,
    properties: Record<string, any> = {}
): void => {
    logger.track(eventName, properties);
    if (typeof window === 'undefined' || !(window as any).ttq) {
        return;
    }

    // Map standard event name to TikTok event first
    const tiktokEvent = TIKTOK_EVENT_MAP[eventName] || 'CustomEvent';

    // Transform properties for TikTok Pixel
    const tiktokProperties: Record<string, any> = {};

    // Get ttq object
    const ttq = (window as any).ttq;
    if (!ttq) {
        return;
    }
    
    // Map common properties
    if (properties.value !== undefined) {
        tiktokProperties.value = properties.value;
    }
    if (properties.currency) {
        tiktokProperties.currency = properties.currency;
    } else {
        tiktokProperties.currency = 'USD';
    }

    // For CompletePayment events, ensure all required fields are populated
    if (tiktokEvent === 'CompletePayment') {
            // Required: value, currency
            tiktokProperties.value = properties.value !== undefined ? properties.value : 0;
            tiktokProperties.currency = properties.currency || 'USD';
            
            // Content properties - use items array if available
            if (properties.items && Array.isArray(properties.items) && properties.items.length > 0) {
                // Use first item for content details (TikTok uses single content_id)
                const firstItem = properties.items[0];
                tiktokProperties.content_name = firstItem.item_name || properties.membership_tier || properties.product_name || '';
                tiktokProperties.content_id = firstItem.item_id || properties.product_id || properties.price_id || '';
                
                // Quantity from items
                tiktokProperties.quantity = firstItem.quantity !== undefined ? firstItem.quantity : 1;
                
                // Contents array for multiple products (TikTok supports this)
                if (properties.items.length > 0) {
                    tiktokProperties.contents = properties.items.map((item: any) => ({
                        content_id: item.item_id || item.id || '',
                        content_name: item.item_name || item.name || '',
                        content_category: item.item_category || item.category || 'membership',
                        price: item.price !== undefined ? item.price : 0,
                        quantity: item.quantity !== undefined ? item.quantity : 1,
                    })).filter((content: any) => content.content_id);
                }
            } else {
                // Fallback if items array not available
                tiktokProperties.content_name = properties.membership_tier || properties.product_name || '';
                tiktokProperties.content_id = properties.product_id || properties.price_id || '';
                tiktokProperties.quantity = 1;
            }
            
            // Content type
            tiktokProperties.content_type = 'product';
            
            // Transaction ID
            if (properties.transaction_id) {
                tiktokProperties.order_id = properties.transaction_id;
            }
        } else {
            // For non-payment events, use simpler mapping
            if (properties.membership_tier) {
                tiktokProperties.content_name = properties.membership_tier;
            }
            if (properties.product_name) {
                tiktokProperties.content_name = properties.product_name;
            }
            if (properties.price_id) {
                tiktokProperties.content_id = properties.price_id;
            }
            if (properties.step !== undefined) {
                tiktokProperties.content_type = `step_${properties.step}`;
            }
    }

    // Track the event
    // TikTok's stub methods automatically queue calls if script not loaded yet
    try {
        ttq.track(tiktokEvent, tiktokProperties);
    } catch (error) {
        console.warn('TikTok Pixel tracking failed:', error);
    }
};

/**
 * Track page view with TikTok Pixel
 */
export const trackTikTokPageView = (properties: Record<string, any> = {}): void => {
    if (typeof window === 'undefined' || !(window as any).ttq) {
        return;
    }

    try {
        (window as any).ttq.page(properties);
    } catch (error) {
        console.warn('TikTok Pixel page view failed:', error);
    }
};

/**
 * Create TikTok Pixel plugin for analytics library
 */
export const createTikTokPixelPlugin = (config: TikTokPixelConfig) => {
    return {
        name: 'tiktok-pixel',
        config,
        initialize: () => {
            initializeTikTokPixel(config);
        },
        track: ({ payload }: any) => {
            trackTikTokEvent(payload.event, payload.properties || {});
        },
        page: ({ payload }: any) => {
            trackTikTokPageView(payload.properties || {});
        },
    };
};

